import React, { useState, useEffect, useMemo, useRef } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { BASEAPIURL, token } from "../env";

const GalleryByEvent = () => {
  const [eventData, setEventData] = useState([]);
  const [facilityData, setFacilityData] = useState([]);
  const [modalImage, setModalImage] = useState(null); // { images: [], index: 0 }
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [shouldLoadEvents, setShouldLoadEvents] = useState(false);
  const [selectedSourceType, setSelectedSourceType] = useState(null); // 'facility' or 'event'
  const eventRef = useRef(null);

  // Lazy load events
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoadEvents(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (eventRef.current) observer.observe(eventRef.current);
    return () => observer.disconnect();
  }, []);

  // Fetch facility list with memoization
  useEffect(() => {
    let mounted = true;
    const fetchFacilityData = async () => {
      setIsLoading(true);
      try {
        const res = await fetch(`${BASEAPIURL}/GetFGrpList?Token=${token}`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        if (!res.ok) throw new Error("Facility fetch failed");
        const result = await res.json();
        const data = Array.isArray(result) ? result : result?.data || [];
        const formatted = data.map((facility, index) => {
          let images = [];
          if (Array.isArray(facility.FacilityImages)) {
            images = facility.FacilityImages.filter((img) => typeof img === "string" && img.trim());
          } else if (typeof facility.FacilityImages === "string") {
            images = facility.FacilityImages.split(",").map((img) => img.trim()).filter(Boolean);
          }
          return {
            id: facility.FacilityGroupID,
            name: facility.FacilityName || `Facility ${index}`,
            images,
          };
        });
        if (mounted) setFacilityData(formatted);
      } catch (err) {
        if (mounted) setError(err.message);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };
    fetchFacilityData();
    return () => {
      mounted = false;
    };
  }, []);

  // Fetch events with memoization
  useEffect(() => {
    if (!shouldLoadEvents) return;
    let mounted = true;
    const fetchEventData = async () => {
      setIsLoading(true);
      try {
        const url = `${BASEAPIURL}/EventList?Token=${encodeURIComponent(token)}`;
        const res = await fetch(url, { method: "GET", headers: { "Content-Type": "application/json" } });
        const result = await res.json();
        const responseData = Array.isArray(result?.data) ? result.data : [];
        const formatted = responseData.map((evt) => {
          let images = [];
          if (typeof evt.EvtImages === "string") {
            images = evt.EvtImages.split(",").map((img) => img.trim()).filter(Boolean);
          } else if (Array.isArray(evt.EvtImages)) {
            images = evt.EvtImages.filter((img) => typeof img === "string" && img.trim());
          }
          return {
            name: evt.EvtTitle || "Unnamed Event",
            images,
          };
        });
        if (mounted) setEventData(formatted);
      } catch (err) {
        if (mounted) setError("Failed to load events.");
      } finally {
        if (mounted) setIsLoading(false);
      }
    };
    fetchEventData();
    return () => {
      mounted = false;
    };
  }, [shouldLoadEvents]);

  // Optimized click handlers
  const handleFacilityClick = (facilityIndex) => {
    const images = facilityData[facilityIndex]?.images || [];
    if (images.length > 0) {
      setModalImage({ images, index: 0 });
      setSelectedSourceType('facility');
    }
  };

  const handleEventClick = (eventIndex) => {
    const images = eventData[eventIndex]?.images || [];
    if (images.length > 0) {
      setModalImage({ images, index: 0 });
      setSelectedSourceType('event');
    }
  };

  // Optimized navigation
  const handlePrev = () => {
    setModalImage(prev => {
      if (!prev) return prev;
      const newIndex = (prev.index - 1 + prev.images.length) % prev.images.length;
      return { ...prev, index: newIndex };
    });
  };

  const handleNext = () => {
    setModalImage(prev => {
      if (!prev) return prev;
      const newIndex = (prev.index + 1) % prev.images.length;
      return { ...prev, index: newIndex };
    });
  };

  const closeModal = () => {
    setModalImage(null);
    setSelectedSourceType(null);
  };

  // Memoized current image
  const currentImage = useMemo(() => {
    return modalImage?.images?.[modalImage.index] || "/placeholder.svg?height=600&width=800";
  }, [modalImage]);

  // Memoized render function
  const renderGallerySection = useMemo(() => {
    return (data, sectionType, title, onClickHandler) => {
      if (!data?.length) return null;
      return (
        <div className="mb-5">
          <h2 className="text-center mb-4 fw-bold text-primary">{title}</h2>
          <div className="row g-4">
            {data.map((item, groupIndex) => (
              <div
                key={`${sectionType}-${groupIndex}`}
                className="col-sm-6 col-md-4 col-lg-3"
              >
                <div
                  className="card h-100 shadow-sm"
                  onClick={() => onClickHandler && onClickHandler(groupIndex)}
                  style={{
                    cursor: item.images?.length ? "pointer" : "not-allowed",
                    opacity: item.images?.length ? 1 : 0.6,
                  }}
                  tabIndex={0}
                  role="button"
                >
                  <LazyLoadImage
                    src={item.images?.[0] || "/placeholder.svg?height=180&width=300"}
                    alt={item.name}
                    className="card-img-top"
                    style={{ height: "200px", objectFit: "cover", borderRadius: "8px" }}
                    onError={(e) => {
                      e.target.src = "/placeholder.svg?height=180&width=300";
                    }}
                  />
                  <div className="card-body">
                    <h5 className="card-title text-center fw-bold">{item.name}</h5>
                    {!item.images?.length && (
                      <p className="text-center text-muted small">No images available</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    };
  }, []);

  // Memoized styles
  const modalStyles = useMemo(() => `
    .gallery-modal {
      position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
      background-color: rgba(0,0,0,0.85); display: flex;
      justify-content: center; align-items: center; z-index: 1050;
    }
    .gallery-modal-content {
      position: relative; max-width: 90vw; max-height: 90vh;
      display: flex; align-items: center; justify-content: center;
    }
    .gallery-modal-image {
      width: 80vw; height: 80vh; object-fit: contain; border-radius: 10px;
    }
    .gallery-close-btn {
      position: absolute; top: 1rem; right: 1rem;
      font-size: 2rem; background: transparent; border: none;
      color: #fff; cursor: pointer;
    }
    .gallery-nav-btn {
      position: absolute; top: 50%; transform: translateY(-50%);
      background: rgba(0,0,0,0.6); border: none; padding: 0.5rem;
      color: white; cursor: pointer; border-radius: 50%;
    }
    .gallery-prev-btn { left: 1rem; }
    .gallery-next-btn { right: 1rem; }
  `, []);

  return (
    <div className="container">
      <style>{modalStyles}</style>

      <h1 className="text-center mt-2 ">Gallery</h1>

      {isLoading && (
        <div className="text-center">
          <div className="spinner-border text-primary" role="status" />
        </div>
      )}
      {error && <div className="alert alert-danger text-center">{error}</div>}

      <div ref={eventRef}>
        {!isLoading && shouldLoadEvents && renderGallerySection(eventData, "event", "", handleEventClick)}
        {!isLoading && !shouldLoadEvents && (
          <div className="text-center text-muted mb-5">Scroll down to load Events...</div>
        )}
      </div>
      {!isLoading && !facilityData.length && !eventData.length && (
        <div className="text-center my-5 text-muted">
          No facilities or events available.
        </div>
      )}

      {modalImage && (
        <div className="gallery-modal" onClick={closeModal}>
          <div
            className="gallery-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="gallery-close-btn"
              onClick={(e) => {
                e.stopPropagation();
                closeModal();
              }}
            >
              ×
            </button>
            <button
              className="gallery-nav-btn gallery-prev-btn"
              onClick={(e) => {
                e.stopPropagation();
                handlePrev();
              }}
            >
              <IoIosArrowBack size={30} />
            </button>
            <LazyLoadImage
              src={currentImage}
              alt="Preview"
              className="gallery-modal-image"
              placeholderSrc="/placeholder.svg?height=600&width=800"
              onError={(e) => {
                e.target.src = "/placeholder.svg?height=600&width=800";
              }}
            />
            <button
              className="gallery-nav-btn gallery-next-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleNext();
              }}
            >
              <IoIosArrowForward size={30} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryByEvent;